import { useEffect } from "react";
import { useAtomValue } from "jotai";
import { AppHeader } from "@pineseed/gui/component";
import { PSAuthConfig } from "@pineseed/workspace/handlers/auth-handler";
import { useAuthApp, usePrivateData, type UseAuthAppReturn } from './hook/useAuthApp';
import { useAdminApp } from './hook/useAdminApp';
import { cn } from "@pineseed/gui/util/utils";
import { AdminProvider } from "./context/AdminContext";
import { AdminPanel } from "./components/AdminPanel";
import { useCopyToClipboard } from "@pineseed/gui/hook/useCopyToClipboard";
import { CopyableText } from "@pineseed/gui/component/CopyableText";
import { ConnectionProvider, useConnectionContext } from "@pineseed/interface-gui/context/connection";
import type { AcpAvatarChatConfig } from "@pineseed/interface-gui/component/AcpAvatarChat";

// Re-export for backward compatibility
export { useAuthApp, usePrivateData };
export type { UseAuthAppReturn };

export default function App({ 
  className,
  pineSeedAuth, 
  httpUrl,
  wsUrl,
  accountId,
  apiKeyTtlS,
  acpAvatarChatConfig,
  children 
}: { 
  className?: string;
  pineSeedAuth: PSAuthConfig;
  httpUrl: string;
  wsUrl: string;
  accountId: string;
  apiKeyTtlS: number;
  acpAvatarChatConfig: AcpAvatarChatConfig;
  children?: React.ReactNode;
}) {
  if (!pineSeedAuth) {
    return <div className="h-full flex items-center justify-center text-red-600 font-bold">Error: pineSeedAuth configuration is required</div>;
  }

  const {
    // Auth state
    isAuthenticated,
    currentUser,
    privateData,
    
    // Participant state
    participantId,
    participantName,
    isParticipantReady,
    isParticipantLoading,
    participantError,
    
    // Auth functions
    authentication,
    
    // Auth context
    AuthContext,
    authContextValue,
    
    // Atoms for AppHeader
    activeWorkspaceTagCountAtom,
    activeWorkspaceAtom,
    allActiveWorkspaceTagsAtom,
    
    // Connection config
    connectionConfig,
    
    // Auth callback handling
    isAuthCallback,
    authCallbackEffect,
  } = useAuthApp({ pineSeedAuth, httpUrl, wsUrl, accountId, apiKeyTtlS });

  // Handle authentication callback
  useEffect(() => {
    if (isAuthCallback) {
      authCallbackEffect();
    }
  }, [isAuthCallback, authCallbackEffect]);

  if (isAuthCallback) {
    return (
      <div>
        <div>Authentication callback - processing...</div>
        <div>This window will close automatically.</div>
      </div>
    );
  }

  return (
    <ConnectionProvider config={connectionConfig}>
      <AuthContext.Provider value={authContextValue}>
        <SystemAdminApp
          className={className}
          pineSeedAuth={pineSeedAuth}
          httpUrl={httpUrl}
          wsUrl={wsUrl}
          acpAvatarChatConfig={acpAvatarChatConfig}
          isAuthenticated={isAuthenticated}
          currentUser={currentUser}
          privateData={privateData}
          participantId={participantId}
          participantName={participantName}
          isParticipantReady={isParticipantReady}
          isParticipantLoading={isParticipantLoading}
          participantError={participantError}
          authentication={authentication}
          activeWorkspaceTagCountAtom={activeWorkspaceTagCountAtom}
          activeWorkspaceAtom={activeWorkspaceAtom}
          allActiveWorkspaceTagsAtom={allActiveWorkspaceTagsAtom}
        >
          {children}
        </SystemAdminApp>
      </AuthContext.Provider>
    </ConnectionProvider>
  );
}

function SystemAdminApp({
  className,
  pineSeedAuth,
  httpUrl,
  wsUrl,
  acpAvatarChatConfig,
  isAuthenticated,
  currentUser,
  privateData,
  participantId,
  participantName,
  isParticipantReady,
  isParticipantLoading,
  participantError,
  authentication,
  activeWorkspaceTagCountAtom,
  activeWorkspaceAtom,
  allActiveWorkspaceTagsAtom,
  children,
}: {
  className?: string;
  pineSeedAuth: PSAuthConfig;
  httpUrl: string;
  wsUrl: string;
  acpAvatarChatConfig: AcpAvatarChatConfig;
  isAuthenticated: boolean;
  currentUser: any;
  privateData: any;
  participantId: any;
  participantName: string;
  isParticipantReady: boolean;
  isParticipantLoading: boolean;
  participantError: string | null;
  authentication: any;
  activeWorkspaceTagCountAtom: UseAuthAppReturn["activeWorkspaceTagCountAtom"];
  activeWorkspaceAtom: UseAuthAppReturn["activeWorkspaceAtom"];
  allActiveWorkspaceTagsAtom: UseAuthAppReturn["allActiveWorkspaceTagsAtom"];
  children?: React.ReactNode;
}) {
  const { isConnected, connectionStatus } = useConnectionContext();

  const adminApp = useAdminApp({ 
    pineSeedAuth, 
    httpUrl, 
    wsUrl,
    isConnected,
  });

  // Log currentUser changes for debugging
  useEffect(() => {
    console.log('👤 System Admin current user updated:', { currentUser, role: currentUser?.role });
  }, [currentUser]);

  // Resolve atoms to values for AppHeader
  const tagCount = useAtomValue(activeWorkspaceTagCountAtom);
  const activeWorkspaceValue = useAtomValue(activeWorkspaceAtom);
  const allTags = useAtomValue(allActiveWorkspaceTagsAtom);

  // Copy feedback
  const { copyToClipboard, isCopied } = useCopyToClipboard();

  // Early return if no current user after auth
  if (isAuthenticated && !currentUser) {
    return <div>Loading user profile...</div>;
  }

  return (
    <div className={cn("h-screen flex flex-col", className)}>
        <AppHeader
            key={`${participantId?.id || 'guest'}-${currentUser?.role || 'guest'}`}
            connectionStatus={connectionStatus}
            authentication={authentication}
            participant={{ id: participantId?.id || '', name: participantName, role: currentUser?.role || 'guest' }}
            clearAuth={authentication.logout}
            setActiveWorkspaceId={() => {}}
            activeWorkspace={true}
            availableWorkspaces={[]}
            tagCount={tagCount}
            activeWorkspaceValue={activeWorkspaceValue}
            allTags={allTags}
            baseUrl="/faces/"
        />
      
      <div className="flex-1 overflow-hidden">
        {/* Gate system admin interface behind authentication */}
        {!isAuthenticated ? (
          <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="max-w-md w-full p-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
              <div className="text-center">
                <div className="mb-6">
                  <svg className="mx-auto h-16 w-16 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  System Admin Access Required
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Please sign in to access the system admin interface.
                </p>
                <button
                  onClick={() => authentication.login()}
                  className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        ) : currentUser?.role !== 'admin' ? (
          <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="max-w-md w-full p-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
              <div className="text-center">
                <div className="mb-6">
                  <svg className="mx-auto h-16 w-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Access Denied
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Admin role required. Your role: {currentUser?.role || 'unknown'}
                </p>
                <button
                  onClick={() => authentication.logout()}
                  className="w-full px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        ) : !isParticipantReady || isParticipantLoading ? (
          <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="text-center">
              <div className="mb-4">
                <svg className="animate-spin h-12 w-12 mx-auto text-blue-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <p className="text-gray-600 dark:text-gray-400">Loading participant data...</p>
            </div>
          </div>
        ) : participantError || !participantId || !isConnected ? (
          <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="max-w-md w-full p-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
              <div className="text-center">
                <div className="mb-6">
                  <svg className="mx-auto h-16 w-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  {!isConnected ? 'Connecting...' : 'Error Loading Profile'}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {!isConnected ? 'Establishing WebSocket connection...' : (participantError || 'Failed to load participant data. Please try signing in again.')}
                </p>
                <div className="space-y-2">
                  <button
                    onClick={() => window.location.reload()}
                    className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Retry
                  </button>
                  <button
                    onClick={() => authentication.logout()}
                    className="w-full px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Authenticated, admin role verified, participant ready, and connected - show system admin interface
          <div className="admin-container h-full">
            <AdminProvider adminApp={adminApp} authConfig={pineSeedAuth} acpAvatarChatConfig={acpAvatarChatConfig} isAuthenticated={isAuthenticated}>
              {children || <AdminPanel />}
            </AdminProvider>
          </div>
        )}
      </div>

      {isAuthenticated && currentUser?.role === 'admin' && privateData?.apiKey && (
        <div className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-2">
          <CopyableText
            id={privateData.apiKey}
            onCopy={copyToClipboard}
            isPulsing={isCopied(privateData.apiKey)}
            variant="muted"
          >
            API Key: {privateData.apiKey.slice(0, 10)}...
          </CopyableText>
        </div>
      )}
    </div>
  );
}
